import { isLocalHost } from '../lib/isLocalHost';
import { publicPath } from '../lib/publicPath';

/**
 * Every environment value the client is allowed to see, parsed once.
 *
 * Nothing secret goes through here. Twilio credentials and the Supabase service
 * key live only in the edge function's environment.
 */

const raw = import.meta.env;

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  const s = str(value);
  if (s === '') return fallback;
  return s === '1' || s.toLowerCase() === 'true';
}

function num(value: unknown, fallback: number): number {
  // An unset var must fall through to the default. Number('') is 0, which would
  // silently mean "idle timeout of zero" and "sample nothing".
  const s = str(value);
  if (s === '') return fallback;
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * `real` — a self-contained vendor JS bundle — was removed on 2026-09-17. It
 * was built for a deliverable that never existed: PixelChefs' scope was the Snap
 * Lens itself, so the AR arrives through Camera Kit and there is no vendor
 * bundle to load. See docs/VENDOR_HANDOFF.md, kept as a record of the contract
 * that was superseded.
 *
 * Note this is NOT the same `'real'` as `arSource` in the upload schema, which
 * is a stub/not-stub flag and reports Camera Kit as `'real'`. That one stays.
 */
export type ArModuleSource = 'stub' | 'camera-kit' | 'remote';
export type TransitionMode = 'opaque' | 'luma';

function localAssetUrl(value: unknown, fallbackRel: string): string {
  const s = str(value);
  if (!s) return publicPath(fallbackRel);
  if (/^https?:\/\//i.test(s)) return s;
  return publicPath(s);
}

function parseArModule(value: unknown): ArModuleSource {
  const s = str(value, 'stub');
  if (s === 'camera-kit') return 'camera-kit';
  // 'remote' renders the lens on another machine and streams it in — the only
  // way to get usable AR on the kiosk panel. See src/ar/remoteModule.ts.
  if (s === 'remote') return 'remote';
  return 'stub';
}

/**
 * Public Snap Lens IDs — not secrets. Token stays in env / .env.local.
 *
 * The lens is the 3D body-tracking version (`Seagal 3d body`), which
 * PixelChefs restored as its own ID on 2026-09-19 after the in-place 2D
 * rebuild (`452e39f5-0386-4f05-9bc0-badefbd30f94`, `The_Incomer_2D`) had
 * replaced it. The 2D version was a concession to the kiosk panel's GPU; the
 * kiosk no longer renders the lens itself and phones never needed it. This
 * default serves the web flow; the kiosk's laptop reads the same value from
 * .env.kiosk via prototype/mirror/server.mjs.
 */
const CK_LENS_GROUP_ID = '3b6ee8f6-bd18-4c67-98b0-8cdd15cde736';
const CK_LENS_ID = 'eea61c70-c895-4826-9b3f-84dd72d088dd';

export const env = {
  arModule: parseArModule(raw.VITE_AR_MODULE),

  ckApiToken: str(raw.VITE_CK_API_TOKEN),
  ckLensGroupId: str(raw.VITE_CK_LENS_GROUP_ID, CK_LENS_GROUP_ID),
  ckLensId: str(raw.VITE_CK_LENS_ID, CK_LENS_ID),
  /**
   * Self-hosted Camera Kit rendering engine, in the standard multi-flavor
   * layout: `<endpoint>/<flavor>/LensCoreWebAssembly.{js,wasm}`.
   *
   * Blank (the default, and correct for the web deploy) uses Snap's CDN.
   * `scripts/fetch-lens-core.mjs` downloads a copy into public/camera-kit/builds
   * for the kiosk, where it takes the largest cold-start fetch off the critical
   * path. Resolved through publicPath so a subdirectory deploy still works.
   */
  ckWasmEndpoint: raw.VITE_CK_WASM_ENDPOINT
    ? localAssetUrl(raw.VITE_CK_WASM_ENDPOINT, 'camera-kit/builds')
    : '',

  defaultMode: str(raw.VITE_MODE, 'web'),

  /**
   * Public host a guest types to redeem a retrieval code. The kiosk shows this
   * on screen, so it must be the deployed site rather than the kiosk's own
   * origin. Blank falls back to the current host, which is right for browser
   * testing and wrong on the APK.
   */
  clipCodeHost: str(raw.VITE_CLIP_CODE_HOST),

  supabaseUrl: str(raw.VITE_SUPABASE_URL),
  supabaseAnonKey: str(raw.VITE_SUPABASE_ANON_KEY),
  supabaseBucket: str(raw.VITE_SUPABASE_BUCKET, 'clips'),

  webUploadEnabled: bool(raw.VITE_WEB_UPLOAD_ENABLED, true),
  webUploadSampleRate: num(raw.VITE_WEB_UPLOAD_SAMPLE_RATE, 1),
  sessionLoggingEnabled: bool(raw.VITE_SESSION_LOGGING_ENABLED, true),
  /**
   * Defaults to `luma` because that is what was actually delivered: the fog
   * master is 1440x1280, two 720x1280 panels side by side (colour left, matte
   * right), and it has to be keyed.
   *
   * It defaulted to `opaque` while the spec was still open with Super
   * Objective, and the flip never happened when the file landed. `.env.local`
   * carried `luma` so every local build was right, `.env.*` is gitignored, and
   * netlify.toml sets nothing — so the deployed build silently played the
   * packed file full-frame. On a phone `object-cover` crops to about the middle
   * 40% of that frame, which puts the join between the two panels straight down
   * the centre of the screen. See TransitionOverlay's metadata guard.
   */
  transitionMode: (str(raw.VITE_TRANSITION_MODE, 'luma') === 'opaque'
    ? 'opaque'
    : 'luma') as TransitionMode,
  /**
   * Whether our own fog transition plays at all. OFF since 2026-09-20: the
   * lens now carries its own smoke, built into the transformation by
   * PixelChefs, and two smokes on top of each other is not the effect. The
   * fog master, `TransitionOverlay` and the luma shader are kept behind this
   * flag; `VITE_TRANSITION_MEDIA=1` brings them back on web. The kiosk's
   * render machine has its own switch for the same thing (`STAGE_FOG` in
   * .env.kiosk). The transition *phase* and its timers stay either way — they
   * pace the flow and the recording, not the picture.
   */
  transitionMediaEnabled: bool(raw.VITE_TRANSITION_MEDIA, false),
  /**
   * Measurement knob only. With the remote AR module the panel draws no
   * transition media (the render machine puts the fog in the stream); this
   * forces the panel to render it anyway, to reproduce the 2026-09-18 clip
   * freeze with the capture-health sampler running. Never ship with it set.
   */
  transitionOnPanelForced: bool(raw.VITE_TRANSITION_ON_PANEL, false),
  /**
   * The dev HUD and the raw error message on ErrorScreen.
   *
   * Localhost is the gate, and VITE_DEV_HUD can only turn it further OFF — it
   * cannot turn it on somewhere else. The build mode travels with the bundle, so
   * the old `import.meta.env.DEV` default put developer chrome on any preview
   * deploy built from a dev config. Where the page is being read from is the
   * thing that actually decides whether a guest is looking at it.
   */
  devHud: isLocalHost() && bool(raw.VITE_DEV_HUD, true),

  kioskCameraDeviceId: str(raw.VITE_KIOSK_CAMERA_DEVICE_ID),
  kioskMicDeviceId: str(raw.VITE_KIOSK_MIC_DEVICE_ID),
  /**
   * The kiosk's recording profile. All software VP8 on four A55 cores — the
   * board has no VP8 encoder — and at 720x1280 that encoder managed about six
   * frames a second, ran seconds behind, and lost whatever was still queued
   * when the recording stopped (measured 2026-09-18 with ffprobe on the clip:
   * 22 video frames in 3.6s, then none for the last 3s while audio ran on).
   * The frame is recorded from a dedicated canvas at this height, 9:16, and
   * the preview stays at 720x1280. Tune against the clip's own frame
   * timestamps, never against the preview's frame rate.
   */
  kioskRecordHeight: num(raw.VITE_KIOSK_RECORD_HEIGHT, 864),
  kioskCaptureFps: num(raw.VITE_KIOSK_CAPTURE_FPS, 30),
  kioskVideoBitsPerSecond: num(raw.VITE_KIOSK_VIDEO_BPS, 2_500_000),
  // 30s matches the convention set on the same hardware by the MHA kiosk.
  kioskIdleTimeoutMs: num(raw.VITE_KIOSK_IDLE_TIMEOUT_MS, 30_000),

  isDev: import.meta.env.DEV,
} as const;

/** True when Supabase is configured enough to attempt a network call. */
export const supabaseConfigured = Boolean(env.supabaseUrl && env.supabaseAnonKey);
